import { Component, ViewChild, inject, signal, OnInit, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { concatMap, finalize, from, map, switchMap, toArray } from 'rxjs';

import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { SourceService, SourceReportResponse } from '../../../service/source.service';
import { SourceContactsService, SourceContact }
  from '../../../service/source-contacts.service';

import { Fountain } from '../../../models/Fountain';
import { ModalEditSource } from '../modal-edit-source/modal-edit-source';

import { DepartmentsGT } from '../../../enum/departments.enum';
import { MunicipalityGT } from '../../../enum/municipalities.enum';
import { GeneralService } from '../../../service/general.service';
import { SourceArticle, SurveyArticulo } from '../../article-view/source-article/source-article';
import { SourceArticlesService } from '../../../features/source-articles/services/source-articles.service';
import { SourceArticleCharacteristicsService } from '../../../features/source-article-characteristics/services/source-article-characteristics.service';
import { getCurrentUserId } from '../../../shared/utils/auth-user.util';
import { ApiResponse } from '../../../shared/models/api-response.model';
import { AgGridAngular } from 'ag-grid-angular';
import {
  AllCommunityModule,
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  GridOptions,
  ICellRendererParams,
  ModuleRegistry
} from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

interface SearchSourceStoredState {
  term: string;
  status: string;
  hasSearched: boolean;
}

@Component({
  selector: 'app-search-source',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ModalEditSource, SourceArticle, AgGridAngular],
  templateUrl: './search-source.html',
  styleUrls: ['./search-source.scss']
})
export class SearchSource implements OnInit {
  private readonly searchStateStorageKey = 'source-search:last-search-state';
  private readonly reportFixedColumns = [
    'fuente_pk',
    'fuente_codigo_INE',
    'fuente_razon_social',
    'fuente_nombre',
    'fuente_direccion',
    'fuente_zona',
    'articulo_pk',
    'articulo_codigo_INE',
    'articulo_nombre',
    'unidad_medida_pk',
    'unidad_medida',
    'factor_conversion',
    'articulo_cantidad',
    'articulo_precio',
    'articulo_comentarios',
    'articulo_estado'
  ];
  private readonly fountainExportColumnOrder = [
    'pk',
    'id',
    'codigoINE',
    'razonSocial',
    'nombre',
    'direccion',
    'fkCodigoPostal',
    'codigoPostalDescripcion',
    'zona',
    'fkDepartamento',
    'departamento',
    'fkMunicipio',
    'municipio',
    'gpsLatitud',
    'gpsLongitud',
    'gpsAltitud',
    'gpsFecha',
    'telefonos',
    'email',
    'fkUsuario',
    'usuarioResponsable',
    'periodo',
    'ordenRuta',
    'fkPeriodicidadVisita',
    'periodicidadVisita',
    'fkDiaVisita',
    'diaVisita',
    'horaInicioPrimeraJornada',
    'horaFinalPrimeraJornada',
    'horaInicioSegundaJornada',
    'horaFinalSegundaJornada',
    'requiereAudio',
    'requiereAudioDescripcion',
    'requiereImagen',
    'requiereImagenDescripcion',
    'nit',
    'fkActividadEmpresa',
    'actividadEmpresa',
    'fkTamanoEmpresa',
    'tamanoEmpresa',
    'codigoCIIU',
    'comentarios',
    'referencia',
    'informacionAdicional',
    'estado',
    'estadoDescripcion',
    'segFuncion',
    'segUsuario',
    'segUsuarioDescripcion',
    'segTransaccion',
    'segDatosAnteriores'
  ];

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private sourceService = inject(SourceService);
  private generalService = inject(GeneralService);
  private contactService = inject(SourceContactsService);
  private sourceArticlesService = inject(SourceArticlesService);
  private sourceArticleCharacteristicsService = inject(SourceArticleCharacteristicsService);

  @ViewChild(SourceArticle)
  sourceArticleCmp?: SourceArticle;

  /** =====================
   *  FORM SEARCH
   *  ===================== */
  searchForm = this.fb.group({
    term: [''],
    status: ['']
  });

  /** =====================
   *  FORM CONTACTO
   *  ===================== */
  form = this.fb.group({
    name: [''],
    title: [''],
    position: [''],
    phones: [''],
    email: [''],
    order: [0],
    comments: [''],
    status: ['A']
  });

  /** =====================
   *  STATE
   *  ===================== */
  loading = signal(false);
  exportingComplete = signal(false);
  fountains = signal<Fountain[]>([]);
  error = signal<string | null>(null);
  hasSearched = signal(false);
  articleSaveError = signal<string | null>(null);
  articleSaveSuccess = signal<string | null>(null);
  articleSaving = signal(false);
  expandedSourceId = signal<number | null>(null);

  readonly paginationPageSize = 10;
  readonly gridOptions: GridOptions<Fountain> = {
    getRowId: (params) => `${this.getSourceId(params.data)}`,
    suppressCellFocus: true
  };
  readonly defaultColDef: ColDef<Fountain> = {
    sortable: true,
    resizable: true,
    suppressMovable: true,
    filter: false,
    floatingFilter: false
  };
  readonly columnDefs: Array<ColDef<Fountain> | ColGroupDef<Fountain>> = [
    {
      headerName: 'Fuente',
      headerClass: 'fountains-grid__group-header fountains-grid__group-header--source',
      marryChildren: true,
      children: [
        {
          headerName: 'Código INE',
          colId: 'open-edit-code',
          field: 'codigoINE',
          minWidth: 150,
          width: 170,
          cellRenderer: (params: ICellRendererParams<Fountain>) =>
            this.renderGridLinkCell(params.value ? String(params.value) : 'Sin código')
        },
        {
          headerName: 'Nombre',
          colId: 'open-edit-name',
          field: 'nombre',
          minWidth: 260,
          flex: 1,
          cellRenderer: (params: ICellRendererParams<Fountain>) =>
            this.renderGridLinkCell(params.value ? String(params.value) : 'Sin nombre')
        }
      ]
    },
    {
      headerName: 'Detalle',
      headerClass: 'fountains-grid__group-header fountains-grid__group-header--detail',
      marryChildren: true,
      children: [
        {
          headerName: 'Estado',
          field: 'estado',
          colId: 'estado-resumen',
          width: 120,
          maxWidth: 130,
          columnGroupShow: 'closed',
          cellRenderer: (params: ICellRendererParams<Fountain>) => this.renderEstadoCell(params.value)
        },
        {
          headerName: 'Razón Social',
          field: 'razonSocial',
          minWidth: 220,
          flex: 1,
          columnGroupShow: 'open'
        },
        {
          headerName: 'Teléfono',
          field: 'telefonos',
          minWidth: 170,
          columnGroupShow: 'open'
        },
        {
          headerName: 'Email',
          field: 'email',
          minWidth: 220,
          flex: 1,
          columnGroupShow: 'open'
        },
        {
          headerName: 'Departamento',
          field: 'fkDepartamento',
          minWidth: 170,
          columnGroupShow: 'open',
          cellRenderer: (params: ICellRendererParams<Fountain>) =>
            this.getDepartamentoName(Number(params.data?.fkDepartamento ?? 0))
        },
        {
          headerName: 'Municipio',
          field: 'fkMunicipio',
          minWidth: 170,
          columnGroupShow: 'open',
          cellRenderer: (params: ICellRendererParams<Fountain>) =>
            this.getMunicipioName(Number(params.data?.fkMunicipio ?? 0))
        },
        {
          headerName: 'Estado',
          field: 'estado',
          colId: 'estado-detalle',
          minWidth: 110,
          width: 120,
          columnGroupShow: 'open',
          cellRenderer: (params: ICellRendererParams<Fountain>) => this.renderEstadoCell(params.value)
        }
      ]
    },
    {
      headerName: 'Acciones',
      colId: 'acciones',
      sortable: false,
      filter: false,
      minWidth: 470,
      width: 500,
      maxWidth: 540,
      cellRenderer: () => this.renderActionsCell()
    }
  ];
  readonly localeText = {
    contains: 'Contiene',
    notContains: 'No contiene',
    equals: 'Igual',
    notEqual: 'Diferente',
    startsWith: 'Empieza con',
    endsWith: 'Termina con',
    filterOoo: 'Filtrar...',
    applyFilter: 'Aplicar',
    resetFilter: 'Limpiar',
    clearFilter: 'Limpiar',
    cancelFilter: 'Cancelar',
    noRowsToShow: 'No hay registros para mostrar',
    page: 'Página',
    more: 'Más',
    to: 'a',
    of: 'de',
    next: 'Siguiente',
    last: 'Último',
    first: 'Primero',
    previous: 'Anterior',
    loadingOoo: 'Cargando...',
    searchOoo: 'Buscar...'
  };
  readonly contactPaginationPageSize = 8;
  readonly contactDefaultColDef: ColDef<SourceContact> = {
    sortable: true,
    resizable: true,
    suppressMovable: true,
    filter: false,
    floatingFilter: false
  };
  readonly contactColumnDefs: ColDef<SourceContact>[] = [
    {
      headerName: 'Nombre',
      field: 'name',
      minWidth: 220,
      flex: 1
    },
    {
      headerName: 'Cargo',
      field: 'position',
      minWidth: 180,
      flex: 1
    },
    {
      headerName: 'Teléfonos',
      field: 'phones',
      minWidth: 180
    },
    {
      headerName: 'Email',
      field: 'email',
      minWidth: 240,
      flex: 1.15
    },
    {
      headerName: 'Orden',
      field: 'order',
      minWidth: 105,
      width: 105,
      maxWidth: 120
    },
    {
      headerName: 'Comentarios',
      field: 'comments',
      minWidth: 240,
      flex: 1.2,
      cellRenderer: (params: ICellRendererParams<SourceContact>) => `${params.value ?? 'Sin comentarios'}`
    },
    {
      headerName: 'Estado',
      field: 'status',
      minWidth: 110,
      width: 110,
      maxWidth: 120,
      cellRenderer: (params: ICellRendererParams<SourceContact>) => this.renderContactStatusCell(params.value)
    },
    {
      headerName: 'Acciones',
      colId: 'contact-actions',
      sortable: false,
      filter: false,
      minWidth: 190,
      width: 210,
      maxWidth: 240,
      cellRenderer: (params: ICellRendererParams<SourceContact>) => this.renderContactActionsCell(params.data)
    }
  ];

  /** FUENTE SELECCIONADA */
  selectedFountain: Fountain | null = null;

  /** CONTACTOS */
  contacts = signal<SourceContact[]>([]);
  loadingContacts = signal(false);
  loadingEditFountainId = signal<number | null>(null);

  /** EDICIÓN */
  editingContact = signal<SourceContact | null>(null);
  previousData = signal<string>('');

  /** AGREGAR INLINE */
  addingContact = signal(false);
  isAdding = false;

  /** CATÁLOGOS */
  usuarios = signal<any[]>([]);
  periodicidades = signal<any[]>([]);
  actividades = signal<any[]>([]);
  tamanios = signal<any[]>([]);
  codigosPostales = signal<any[]>([]);
  diasVisita = signal<any[]>([]);

  ngOnInit(): void {
    this.loadCatalogs();
    this.initTooltips();
    this.restoreSearchState();
  }

  /** =====================
   *  CATÁLOGOS
   *  ===================== */
  loadCatalogs(): void {
    this.loadCached('GET_USERS', this.usuarios);
    this.loadCached('GET_PERIODICIDAD_VISITA', this.periodicidades);
    this.loadCached('GET_ACTIVIDADES_EMPRESA', this.actividades);
    this.loadCached('GET_TAMANO_EMPRESA', this.tamanios);
    this.loadCached('GET_CODIGOS_POSTALES', this.codigosPostales);
    this.loadCached('GET_DIAS_VISITA', this.diasVisita);
  }

  private loadCached(key: string, target: any): void {
    const cached = localStorage.getItem(key);
    if (cached) {
      target.set(JSON.parse(cached));
      return;
    }

    this.generalService.get(key).subscribe(res => {
      localStorage.setItem(key, JSON.stringify(res));
      target.set(res);
    });
  }

  /** =====================
   *  BÚSQUEDA
   *  ===================== */
  onSearch(): void {
    const term = this.searchForm.value.term?.trim() || '';
    const status = term ? '' : (this.searchForm.value.status?.trim().toUpperCase() || '');
    this.fetchFountains({ term, status });
  }

  clearFilters(): void {
    this.searchForm.patchValue({
      term: '',
      status: ''
    });
    this.loading.set(false);
    this.error.set(null);
    this.hasSearched.set(false);
    this.fountains.set([]);
    this.selectedFountain = null;
    this.clearStoredSearchState();
    this.closeArticleRow();
  }

  /** ✅ Cargar fuentes SIEMPRE DESDE getAll() y filtrar en frontend */
  private fetchFountains(opts: { term?: string; status?: string }): void {
    const term = (opts.term ?? '').trim();
    const status = (opts.status ?? '').trim().toUpperCase();

    this.storeSearchState({ term, status, hasSearched: true });
    this.loading.set(true);
    this.error.set(null);
    this.hasSearched.set(true);

    this.sourceService.getAll().subscribe({
      next: data => {
        const list = this.applyFilters((data ?? []) as any[], term, status);

        this.fountains.set(this.sortLatestFirst(list) as Fountain[]);
        this.syncExpandedSourceWithResults();
        this.initTooltips();
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Error al consultar registros.');
      }
    });
  }

  /** =====================
   *  EXCEL XLSX COMPLETO DE MANTENIMIENTO (SIEMPRE DESDE getAll)
   *  ===================== */
  exportExcel(): void {
    this.loading.set(true);
    this.error.set(null);

    this.sourceService.getAll().subscribe({
      next: async data => {
        const allFountains = this.sortLatestFirst((data ?? []) as any[]);
        const rows = allFountains.map((fountain) => this.buildFountainExportRow(fountain));

        if (!rows.length) {
          this.error.set('No hay fuentes para exportar.');
          this.loading.set(false);
          return;
        }

        try {
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet('Fuentes');
          const headers = Object.keys(rows[0]);

          worksheet.columns = headers.map((header) => ({
            header,
            key: header,
            width: this.calculateExportColumnWidth(header, rows)
          }));

          worksheet.addRows(rows);
          worksheet.views = [{ state: 'frozen', ySplit: 1, topLeftCell: 'A2' }];
          worksheet.autoFilter = {
            from: { row: 1, column: 1 },
            to: { row: 1, column: headers.length }
          };

          const headerRow = worksheet.getRow(1);
          headerRow.font = { bold: true };
          headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          headerRow.height = 24;

          worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            if (rowNumber === 1) {
              return;
            }

            row.alignment = { vertical: 'top', wrapText: true };
          });

          const buffer = await workbook.xlsx.writeBuffer();
          const name = `fuentes_mantenimiento_completo_${new Date().toISOString().slice(0, 10)}.xlsx`;

          saveAs(
            new Blob([buffer], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
            name
          );
        } catch {
          this.error.set('Error al exportar Excel.');
        } finally {
          this.loading.set(false);
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Error al exportar Excel.');
      }
    });
  }

  exportCompleteExcel(): void {
    const visibleSourceIds = this.getVisibleSourceIds();

    if (this.hasSearched() && visibleSourceIds.length === 0) {
      this.error.set('No hay fuentes para exportar con los filtros actuales.');
      return;
    }

    const pkFuente = visibleSourceIds.length === 1 ? visibleSourceIds[0] : undefined;
    const shouldFilterVisible = this.hasSearched() && visibleSourceIds.length > 1;

    this.exportingComplete.set(true);
    this.error.set(null);

    this.sourceService.getSourceArticleCharacteristicsReport(pkFuente).subscribe({
      next: async (response) => {
        try {
          const extractedRows = this.extractReportRows(response);

          if (!extractedRows) {
            this.error.set(this.extractReportMessage(response) || 'No fue posible obtener el reporte completo.');
            return;
          }

          const rows = shouldFilterVisible
            ? extractedRows.filter((row) => visibleSourceIds.includes(Number(row?.['fuente_pk'] ?? 0)))
            : extractedRows;

          if (!rows.length) {
            this.error.set('No hay datos para exportar en el reporte completo.');
            return;
          }

          const headers = this.getReportHeaders(rows, response);
          const normalizedRows = this.normalizeReportRows(rows, headers);
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet('FuentesArticulos');

          worksheet.columns = headers.map((header) => ({
            header: this.formatReportHeaderLabel(header),
            key: header,
            width: this.calculateReportColumnWidth(header, normalizedRows)
          }));

          worksheet.addRows(normalizedRows);
          worksheet.views = [{ state: 'frozen', ySplit: 1, topLeftCell: 'A2' }];
          worksheet.autoFilter = {
            from: { row: 1, column: 1 },
            to: { row: 1, column: headers.length }
          };

          const headerRow = worksheet.getRow(1);
          headerRow.font = { bold: true };
          headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          headerRow.height = 24;

          worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            if (rowNumber === 1) {
              return;
            }

            row.alignment = { vertical: 'top', wrapText: true };
          });

          const buffer = await workbook.xlsx.writeBuffer();
          const today = new Date().toISOString().slice(0, 10);
          const name = pkFuente != null
            ? `fuente_${pkFuente}_articulos_caracteristicas_${today}.xlsx`
            : `fuentes_articulos_caracteristicas_${today}.xlsx`;

          saveAs(
            new Blob([buffer], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
            name
          );
        } catch {
          this.error.set('Error al construir el Excel completo.');
        } finally {
          this.exportingComplete.set(false);
        }
      },
      error: (error) => {
        this.exportingComplete.set(false);
        this.error.set(this.extractReportMessage(error?.error) || 'Error al exportar el Excel completo.');
      }
    });
  }

  openSourceArticles(f: Fountain): void {
    const sourceId = (f as any).id ?? (f as any).pk;
    if (!sourceId) return;

    this.router.navigate(['/fuentes-articulos'], {
      queryParams: { fkFuente: sourceId }
    });
  }

  openNewSourceArticle(f: Fountain): void {
    this.toggleArticlesRow(f);
  }

  onGridCellClicked(event: CellClickedEvent<Fountain>): void {
    if ((event.colDef.colId === 'open-edit-code' || event.colDef.colId === 'open-edit-name') && event.data) {
      this.openEditModal(event.data);
      return;
    }

    if (event.colDef.colId !== 'acciones' || !event.data) {
      return;
    }

    const actionButton = (event.event?.target as HTMLElement | null)?.closest<HTMLButtonElement>('[data-action]');
    const action = actionButton?.dataset['action'];

    if (action === 'edit') {
      this.openEditModal(event.data);
      return;
    }

    if (action === 'contacts') {
      this.openContactsModal(event.data);
      return;
    }

    if (action === 'consult') {
      this.openSourceArticles(event.data);
      return;
    }

    if (action === 'article') {
      this.openNewSourceArticle(event.data);
    }
  }

  toggleArticlesRow(f: Fountain): void {
    const sourceId = this.getSourceId(f);
    if (!sourceId) return;

    this.articleSaveError.set(null);
    this.articleSaveSuccess.set(null);

    if (this.expandedSourceId() === sourceId) {
      this.closeArticleRow();
      return;
    }

    this.selectedFountain = f;
    this.expandedSourceId.set(sourceId);
    setTimeout(() => this.initTooltips());
  }

  closeArticleRow(): void {
    this.expandedSourceId.set(null);
    this.articleSaveError.set(null);
    this.articleSaveSuccess.set(null);
    this.sourceArticleCmp?.reset();
    setTimeout(() => this.initTooltips());
  }

  saveExpandedArticles(): void {
    const sourceId = this.expandedSourceId();
    if (!sourceId) return;

    const pendingArticles = this.sourceArticleCmp?.buildArticulosPayload() ?? [];
    if (!this.countArticleCharacteristics(pendingArticles)) {
      this.articleSaveError.set('Agregue al menos un artículo con una característica antes de guardar.');
      return;
    }

    this.articleSaving.set(true);
    this.articleSaveError.set(null);
    this.articleSaveSuccess.set(null);

    this.saveArticlesSequentially(sourceId, pendingArticles)
      .pipe(finalize(() => this.articleSaving.set(false)))
      .subscribe({
        next: () => {
          this.sourceArticleCmp?.resetPending();
          this.articleSaveSuccess.set('Artículos agregados correctamente a la fuente.');
          setTimeout(() => this.initTooltips());
        },
        error: (error) => {
          const message = error?.error?.message ?? error?.message ?? 'No fue posible guardar los artículos de la fuente.';
          this.sourceArticleCmp?.handleSaveFailure(message);
          this.articleSaveError.set(message);
        }
      });
  }

  private restoreSearchState(): void {
    const storedState = this.getStoredSearchState();

    if (!storedState?.hasSearched) {
      return;
    }

    const term = storedState.term.trim();
    const status = term ? '' : storedState.status.trim().toUpperCase();

    this.searchForm.patchValue({ term, status }, { emitEvent: false });
    this.fetchFountains({ term, status });
  }

  private getStoredSearchState(): SearchSourceStoredState | null {
    try {
      const rawState = sessionStorage.getItem(this.searchStateStorageKey);
      if (!rawState) {
        return null;
      }

      const parsedState = JSON.parse(rawState) as Partial<SearchSourceStoredState>;
      return {
        term: `${parsedState.term ?? ''}`,
        status: `${parsedState.status ?? ''}`,
        hasSearched: parsedState.hasSearched === true
      };
    } catch {
      return null;
    }
  }

  private storeSearchState(state: SearchSourceStoredState): void {
    try {
      sessionStorage.setItem(this.searchStateStorageKey, JSON.stringify(state));
    } catch {
      // If browser storage is unavailable, the search still works normally.
    }
  }

  private clearStoredSearchState(): void {
    try {
      sessionStorage.removeItem(this.searchStateStorageKey);
    } catch {
      // Ignore storage cleanup errors.
    }
  }

  /** =====================
   *  CONTACTOS
   *  ===================== */
  openContactsModal(f: Fountain): void {
    const sourceId = (f as any).id ?? (f as any).pk;
    if (!sourceId) return;

    this.selectedFountain = f;
    this.loadingContacts.set(true);
    this.contacts.set([]);
    this.addingContact.set(false);

    this.contactService.getBySource(sourceId).subscribe({
      next: res => {
        this.contacts.set(res ?? []);
        this.loadingContacts.set(false);

        setTimeout(() => {
          const el = document.getElementById('contactsModal');
          if (!el) return;

          let modal = (window as any).bootstrap.Modal.getInstance(el);
          if (!modal) {
            modal = new (window as any).bootstrap.Modal(el, {
              backdrop: 'static',
              keyboard: false
            });
          }
          modal.show();
          this.initTooltips();
        });
      },
      error: () => {
        this.loadingContacts.set(false);
      }
    });
  }


  /** =====================
   *  AGREGAR CONTACTO INLINE
   *  ===================== */
  openAddContactInline(): void {
    this.editingContact.set(null);
    this.form.reset({ order: 0, status: 'A' });
    this.addingContact.set(true);
    this.initTooltips();
  }

  cancelAddContact(): void {
    this.editingContact.set(null);
    this.form.reset({ order: 0, status: 'A' });
    this.addingContact.set(false);
    this.initTooltips();
  }

  addContact(): void {
    if (!this.selectedFountain) return;

    this.isAdding = true;
    const userId = 1;
    const sourceId = (this.selectedFountain as any).id ?? (this.selectedFountain as any).pk;
    const editing = this.editingContact();
    const dto = {
      ...this.form.getRawValue(),
      sourceId,
      status: this.normalizeContactStatus(this.form.getRawValue().status)
    };

    const request$ = editing
      ? this.contactService.update(
          {
            ...editing,
            ...dto,
            previousData: this.previousData()
          } as any,
          userId
        )
      : this.contactService.create(dto as any, userId);

    request$.subscribe(() => {
      this.addingContact.set(false);
      this.editingContact.set(null);
      this.isAdding = false;
      this.openContactsModal(this.selectedFountain!);
    });
  }

  startEditContact(c: SourceContact): void {
    this.previousData.set(this.buildPreviousData(c));
    this.editingContact.set({ ...c });
    this.form.reset({
      name: c.name ?? '',
      title: c.title ?? '',
      position: c.position ?? '',
      phones: c.phones ?? '',
      email: c.email ?? '',
      order: Number(c.order ?? 0),
      comments: c.comments ?? '',
      status: this.normalizeContactStatus(c.status)
    });
    this.addingContact.set(true);
    this.initTooltips();
  }

  deactivateContact(c: SourceContact): void {
    const userId = 1;
    this.contactService.deactivate(c.id!, userId).subscribe(() => {
      this.contacts.update((items) =>
        items.map((item) =>
          item.id === c.id ? { ...item, status: 'I' } : item
        )
      );
    });
  }

  activateContact(c: SourceContact): void {
    if (!this.selectedFountain) return;

    const userId = 1;
    const sourceId = (this.selectedFountain as any).id ?? (this.selectedFountain as any).pk;

    this.contactService.update(
      {
        ...c,
        sourceId,
        status: 'A',
        previousData: this.buildPreviousData(c)
      } as any,
      userId
    ).subscribe(() => {
      this.contacts.update((items) =>
        items.map((item) =>
          item.id === c.id ? { ...item, status: 'A' } : item
        )
      );
    });
  }

  /** =====================
   *  UTILIDADES
   *  ===================== */
  private buildPreviousData(c: SourceContact): string {
    return [
      c.id ?? '',
      c.title ?? '',
      c.name ?? '',
      c.position ?? '',
      c.phones ?? '',
      c.email ?? '',
      c.comments ?? '',
      c.order ?? '',
      c.status ?? ''
    ].join('|');
  }

  private normalizeContactStatus(status: unknown): 'A' | 'I' {
    const normalized = String(status ?? '').toUpperCase();
    return normalized === 'I' ? 'I' : 'A';
  }


  visibleContacts = computed(() => this.contacts());

  // activeContacts = computed(() =>
  //   this.contacts().filter(c => c.status === 'A')
  // );

  getPeriodicidadName(id: number) {
    return this.periodicidades().find(x => x.pk === id)?.descripcion ?? id;
  }

  getCodigoPostalName(id: number): string | number {
    return this.getCatalogDescription(this.codigosPostales(), id);
  }

  getUsuarioName(id: number): string | number {
    return this.getCatalogDescription(this.usuarios(), id, ['nombre', 'descripcion']);
  }

  getDiaVisitaName(id: number): string | number {
    return this.getCatalogDescription(this.diasVisita(), id);
  }

  getActividadEmpresaName(id: number): string | number {
    return this.getCatalogDescription(this.actividades(), id);
  }

  getTamanoEmpresaName(id: number): string | number {
    return this.getCatalogDescription(this.tamanios(), id);
  }

  getEstadoName(value: any): string {
    const estado = String(value ?? '').toUpperCase();
    if (estado === 'A') return 'Activo';
    if (estado === 'P') return 'Pendiente';
    if (estado === 'I') return 'Inactivo';
    return 'N/D';
  }

  getEstadoClass(value: any): string {
    const estado = String(value ?? '').toUpperCase();
    if (estado === 'A') return 'text-success';
    if (estado === 'P') return 'text-warning';
    if (estado === 'I') return 'text-danger';
    return 'text-body-secondary';
  }

  getDepartamentoName(id: number) {
    return (DepartmentsGT as any)[id] ?? id;
  }

  getMunicipioName(id: number) {
    return (MunicipalityGT as any)[id] ?? id;
  }

  openEditModal(f: Fountain): void {
    const sourceId = this.getSourceId(f);
    this.selectedFountain = f;

    if (!sourceId) {
      this.showEditModal();
      return;
    }

    this.loadingEditFountainId.set(sourceId);

    this.sourceService.getById(sourceId).subscribe({
      next: (detail) => {
        this.selectedFountain = {
          ...f,
          ...detail,
          id: Number((detail as any)?.id ?? f.id ?? sourceId),
          pk: Number((detail as any)?.pk ?? f.pk ?? sourceId)
        };
        this.loadingEditFountainId.set(null);
        this.showEditModal();
      },
      error: () => {
        this.loadingEditFountainId.set(null);
        this.showEditModal();
      }
    });
  }

  onFountainUpdated(updatedFountain: Fountain): void {
    const sourceId = this.getSourceId(updatedFountain);
    const term = this.searchForm.value.term?.trim() || '';
    const status = term ? '' : (this.searchForm.value.status?.trim().toUpperCase() || '');

    this.selectedFountain = updatedFountain;

    this.fountains.update((currentFountains) => {
      const mergedFountains = currentFountains.map((fountain) =>
        this.getSourceId(fountain) === sourceId
          ? { ...fountain, ...updatedFountain }
          : fountain
      );

      return this.sortLatestFirst(this.applyFilters(mergedFountains as any[], term, status)) as Fountain[];
    });

    if (this.expandedSourceId() === sourceId) {
      this.selectedFountain = {
        ...(this.selectedFountain ?? updatedFountain),
        ...updatedFountain
      };
    }

    this.initTooltips();
  }

  onFountainDeleted(): void {
    const sourceId = this.getSourceId(this.selectedFountain);

    if (!sourceId) {
      this.selectedFountain = null;
      return;
    }

    this.fountains.update((currentFountains) =>
      currentFountains.filter((fountain) => this.getSourceId(fountain) !== sourceId)
    );

    if (this.expandedSourceId() === sourceId) {
      this.closeArticleRow();
    }

    this.selectedFountain = null;
    this.initTooltips();
  }

  private showEditModal(): void {
    setTimeout(() => {
      const el = document.getElementById('editModal');
      if (!el) return;

      let modal = (window as any).bootstrap.Modal.getInstance(el);
      if (!modal) {
        modal = new (window as any).bootstrap.Modal(el, {
          backdrop: 'static',
          keyboard: false
        });
      }

      modal.show();
      this.initTooltips();
    });
  }

  getSourceId(f: Fountain | null | undefined): number {
    return Number((f as any)?.id ?? (f as any)?.pk ?? 0);
  }

  getVisibleFountainsLabel(): string {
    const count = this.fountains().length;

    if (!this.hasSearched()) {
      return 'Listo para consultar';
    }

    return count === 1 ? '1 fuente visible' : `${count} fuentes visibles`;
  }

  getSelectedFountainLabel(): string {
    return this.getFountainDisplayLabel(this.selectedFountain);
  }

  getActiveFilterSummary(): string {
    const term = this.searchForm.value.term?.trim() || '';
    const status = this.searchForm.value.status?.trim().toUpperCase() || '';

    if (term) {
      return `Filtro por termino: ${term}`;
    }

    if (status) {
      return `Filtro por estado: ${this.getEstadoName(status)}`;
    }

    return this.hasSearched() ? 'Mostrando todas las fuentes' : 'Sin filtros aplicados';
  }

  getCompleteExportSummary(): string {
    const visibleCount = this.fountains().length;

    if (!this.hasSearched()) {
      return 'La exportacion completa incluira todas las fuentes con articulos y caracteristicas.';
    }

    if (visibleCount === 0) {
      return 'Ajuste los filtros para generar el reporte completo.';
    }

    if (visibleCount === 1) {
      return 'La exportacion completa usara la unica fuente visible del filtro actual.';
    }

    return 'La exportacion completa respetara las fuentes visibles del filtro actual.';
  }

  private applyFilters(list: any[], term: string, status: string): any[] {
    let filtered = [...list];

    if (term) {
      const normalizedTerm = term.toLowerCase();
      filtered = filtered.filter(f =>
        (f.nombre ?? '').toLowerCase().includes(normalizedTerm) ||
        (f.codigoINE ?? '').toLowerCase().includes(normalizedTerm)
      );
      return filtered;
    }

    if (status) {
      filtered = filtered.filter(f => String(f?.estado ?? '').toUpperCase() === status);
    }

    return filtered;
  }

  private sortLatestFirst(list: any[]): any[] {
    return [...list].sort((a, b) => {
      const left = Number(b?.pk ?? b?.id ?? 0);
      const right = Number(a?.pk ?? a?.id ?? 0);
      return left - right;
    });
  }

  private syncExpandedSourceWithResults(): void {
    const expandedSourceId = this.expandedSourceId();

    if (!expandedSourceId) {
      return;
    }

    const visibleSource = this.fountains().find((fountain) => this.getSourceId(fountain) === expandedSourceId) ?? null;

    if (!visibleSource) {
      this.closeArticleRow();
      return;
    }

    this.selectedFountain = visibleSource;
  }

  private getFountainDisplayLabel(fountain: Fountain | null | undefined): string {
    const codigo = `${(fountain as any)?.codigoINE ?? ''}`.trim();
    const nombre = `${(fountain as any)?.nombre ?? (fountain as any)?.razonSocial ?? ''}`.trim();
    const sourceId = this.getSourceId(fountain);

    if (codigo && nombre) {
      return `${codigo} - ${nombre}`;
    }

    if (nombre) {
      return nombre;
    }

    if (codigo) {
      return codigo;
    }

    return sourceId > 0 ? `Fuente #${sourceId}` : 'Fuente seleccionada';
  }

  private renderEstadoCell(value: unknown): string {
    const estadoClass = this.getEstadoClass(value);
    const estadoName = this.getEstadoName(value);
    return `<span class="${estadoClass} fw-semibold">${estadoName}</span>`;
  }

  private renderActionsCell(): string {
    return `
      <div class="fountains-grid__actions">
        ${this.buildActionButtonHtml('edit', 'bi bi-pencil', 'btn-outline-info', 'Editar', 'Editar fuente')}
        ${this.buildActionButtonHtml('contacts', 'bi bi-people', 'btn-outline-info', 'Contactos', 'Consultar contactos')}
        ${this.buildActionButtonHtml('consult', 'bi bi-list-check', 'btn-outline-info', 'Artículo', 'Consultar artículos')}
        ${this.buildActionButtonHtml('article', 'bi bi-plus-lg', 'btn-outline-info', 'Agregar', 'Agregar artículo')}
      </div>
    `;
  }

  onContactGridCellClicked(event: CellClickedEvent<SourceContact>): void {
    if (event.colDef.colId !== 'contact-actions' || !event.data) {
      return;
    }

    const actionButton = (event.event?.target as HTMLElement | null)?.closest<HTMLButtonElement>('[data-action]');
    const action = actionButton?.dataset['action'];

    if (action === 'edit') {
      this.startEditContact(event.data);
      return;
    }

    if (action === 'toggle-status') {
      const currentStatus = String(event.data.status ?? '').toUpperCase();
      if (currentStatus === 'A') {
        this.deactivateContact(event.data);
      } else {
        this.activateContact(event.data);
      }
    }
  }

  private renderContactStatusCell(value: unknown): string {
    const statusClass = this.getContactStatusBadgeClass(value);
    const statusName = this.getEstadoName(value);
    return `<span class="badge ${statusClass}">${statusName}</span>`;
  }

  private getContactStatusBadgeClass(value: unknown): string {
    const estado = String(value ?? '').toUpperCase();
    if (estado === 'A') return 'text-bg-success';
    if (estado === 'P') return 'text-bg-warning';
    if (estado === 'I') return 'text-bg-secondary';
    return 'text-bg-secondary';
  }

  private renderContactActionsCell(contact: SourceContact | undefined): string {
    const isActive = String(contact?.status ?? '').toUpperCase() === 'A';

    return `
      <div class="contacts-grid__actions">
        ${this.buildContactActionButtonHtml('edit', 'bi bi-pencil', 'btn-outline-info', 'Editar', 'Editar contacto')}
        ${this.buildContactActionButtonHtml(
          'toggle-status',
          isActive ? 'bi bi-trash' : 'bi bi-arrow-clockwise',
          'btn-outline-info',
          isActive ? 'Desactivar' : 'Activar',
          isActive ? 'Desactivar contacto' : 'Activar contacto'
        )}
      </div>
    `;
  }

  private buildContactActionButtonHtml(
    action: 'edit' | 'toggle-status',
    iconClass: string,
    buttonClass: string,
    label: string,
    tooltip: string
  ): string {
    return `
      <button
        type="button"
        class="btn btn-sm ${buttonClass}"
        data-action="${action}"
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        data-app-tooltip="true"
        title="${tooltip}"
        aria-label="${tooltip}">
        <i class="${iconClass} me-1"></i>${label}
      </button>
    `;
  }

  private buildActionButtonHtml(
    action: 'edit' | 'contacts' | 'consult' | 'article',
    iconClass: string,
    buttonClass: string,
    label: string,
    tooltip: string
  ): string {
    return `
      <button
        type="button"
        class="btn btn-sm ${buttonClass}"
        data-action="${action}"
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        data-app-tooltip="true"
        title="${tooltip}"
        aria-label="${tooltip}">
        <i class="${iconClass} me-1"></i>${label}
      </button>
    `;
  }

  private renderGridLinkCell(label: string): string {
    return `
      <button type="button" class="btn btn-link btn-sm fountains-grid__link p-0">
        ${this.escapeHtml(label)}
      </button>
    `;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private getVisibleSourceIds(): number[] {
    return this.fountains()
      .map((fountain) => this.getSourceId(fountain))
      .filter((sourceId) => sourceId > 0);
  }

  private buildFountainExportRow(fountain: Fountain): Record<string, string | number> {
    const raw = fountain as any;
    const row: Record<string, string | number> = {
      pk: Number(raw?.pk ?? raw?.id ?? 0) || '',
      id: Number(raw?.id ?? raw?.pk ?? 0) || '',
      codigoINE: raw?.codigoINE ?? '',
      razonSocial: raw?.razonSocial ?? '',
      nombre: raw?.nombre ?? '',
      direccion: raw?.direccion ?? '',
      fkCodigoPostal: this.toExportNumber(raw?.fkCodigoPostal),
      codigoPostalDescripcion: this.toExportText(this.getCodigoPostalName(Number(raw?.fkCodigoPostal ?? 0))),
      zona: this.toExportNumber(raw?.zona),
      fkDepartamento: this.toExportNumber(raw?.fkDepartamento),
      departamento: this.toExportText(this.getDepartamentoName(Number(raw?.fkDepartamento ?? 0))),
      fkMunicipio: this.toExportNumber(raw?.fkMunicipio),
      municipio: this.toExportText(this.getMunicipioName(Number(raw?.fkMunicipio ?? 0))),
      gpsLatitud: this.toExportNumber(raw?.gpsLatitud),
      gpsLongitud: this.toExportNumber(raw?.gpsLongitud),
      gpsAltitud: this.toExportNumber(raw?.gpsAltitud),
      gpsFecha: raw?.gpsFecha ?? '',
      telefonos: raw?.telefonos ?? '',
      email: raw?.email ?? '',
      fkUsuario: this.toExportNumber(raw?.fkUsuario),
      usuarioResponsable: this.toExportText(this.getUsuarioName(Number(raw?.fkUsuario ?? 0))),
      periodo: this.toExportNumber(raw?.periodo),
      ordenRuta: this.toExportNumber(raw?.ordenRuta),
      fkPeriodicidadVisita: this.toExportNumber(raw?.fkPeriodicidadVisita),
      periodicidadVisita: this.toExportText(this.getPeriodicidadName(Number(raw?.fkPeriodicidadVisita ?? 0))),
      fkDiaVisita: this.toExportNumber(raw?.fkDiaVisita),
      diaVisita: this.toExportText(this.getDiaVisitaName(Number(raw?.fkDiaVisita ?? 0))),
      horaInicioPrimeraJornada: raw?.horaInicioPrimeraJornada ?? '',
      horaFinalPrimeraJornada: raw?.horaFinalPrimeraJornada ?? '',
      horaInicioSegundaJornada: raw?.horaInicioSegundaJornada ?? '',
      horaFinalSegundaJornada: raw?.horaFinalSegundaJornada ?? '',
      requiereAudio: raw?.requiereAudio ?? '',
      requiereAudioDescripcion: this.getYesNoLabel(raw?.requiereAudio),
      requiereImagen: raw?.requiereImagen ?? '',
      requiereImagenDescripcion: this.getYesNoLabel(raw?.requiereImagen),
      nit: raw?.nit ?? '',
      fkActividadEmpresa: this.toExportNumber(raw?.fkActividadEmpresa),
      actividadEmpresa: this.toExportText(this.getActividadEmpresaName(Number(raw?.fkActividadEmpresa ?? 0))),
      fkTamanoEmpresa: this.toExportNumber(raw?.fkTamanoEmpresa),
      tamanoEmpresa: this.toExportText(this.getTamanoEmpresaName(Number(raw?.fkTamanoEmpresa ?? 0))),
      codigoCIIU: raw?.codigoCIIU ?? '',
      comentarios: raw?.comentarios ?? '',
      referencia: raw?.referencia ?? '',
      informacionAdicional: raw?.informacionAdicional ?? '',
      estado: raw?.estado ?? '',
      estadoDescripcion: this.getEstadoName(raw?.estado),
      segFuncion: raw?.segFuncion ?? '',
      segUsuario: this.toExportNumber(raw?.segUsuario),
      segUsuarioDescripcion: this.toExportText(this.getUsuarioName(Number(raw?.segUsuario ?? 0))),
      segTransaccion: this.toExportNumber(raw?.segTransaccion),
      segDatosAnteriores: raw?.segDatosAnteriores ?? ''
    };

    return this.fountainExportColumnOrder.reduce<Record<string, string | number>>((orderedRow, key) => {
      orderedRow[this.getFountainExportLabel(key)] = row[key] ?? '';
      return orderedRow;
    }, {});
  }

  private getFountainExportLabel(key: string): string {
    const labels: Record<string, string> = {
      pk: 'PK',
      id: 'ID',
      codigoINE: 'Código INE',
      razonSocial: 'Razón Social',
      nombre: 'Nombre',
      direccion: 'Dirección',
      fkCodigoPostal: 'FK Código Postal',
      codigoPostalDescripcion: 'Código Postal',
      zona: 'Zona',
      fkDepartamento: 'FK Departamento',
      departamento: 'Departamento',
      fkMunicipio: 'FK Municipio',
      municipio: 'Municipio',
      gpsLatitud: 'GPS Latitud',
      gpsLongitud: 'GPS Longitud',
      gpsAltitud: 'GPS Altitud',
      gpsFecha: 'GPS Fecha',
      telefonos: 'Teléfonos',
      email: 'Email',
      fkUsuario: 'FK Usuario Responsable',
      usuarioResponsable: 'Usuario Responsable',
      periodo: 'Periodo',
      ordenRuta: 'Orden de Ruta',
      fkPeriodicidadVisita: 'FK Periodicidad de Visita',
      periodicidadVisita: 'Periodicidad de Visita',
      fkDiaVisita: 'FK Día de Visita',
      diaVisita: 'Día de Visita',
      horaInicioPrimeraJornada: 'Hora Inicio Primera Jornada',
      horaFinalPrimeraJornada: 'Hora Final Primera Jornada',
      horaInicioSegundaJornada: 'Hora Inicio Segunda Jornada',
      horaFinalSegundaJornada: 'Hora Final Segunda Jornada',
      requiereAudio: 'Requiere Audio',
      requiereAudioDescripcion: 'Requiere Audio Descripción',
      requiereImagen: 'Requiere Imagen',
      requiereImagenDescripcion: 'Requiere Imagen Descripción',
      nit: 'NIT',
      fkActividadEmpresa: 'FK Actividad Empresa',
      actividadEmpresa: 'Actividad Empresa',
      fkTamanoEmpresa: 'FK Tamaño Empresa',
      tamanoEmpresa: 'Tamaño Empresa',
      codigoCIIU: 'Código CIIU',
      comentarios: 'Comentarios',
      referencia: 'Referencia',
      informacionAdicional: 'Información Adicional',
      estado: 'Estado',
      estadoDescripcion: 'Estado Descripción',
      segFuncion: 'Seg Función',
      segUsuario: 'Seg Usuario',
      segUsuarioDescripcion: 'Seg Usuario Descripción',
      segTransaccion: 'Seg Transacción',
      segDatosAnteriores: 'Seg Datos Anteriores'
    };

    return labels[key] ?? key;
  }

  private getCatalogDescription(
    catalog: any[],
    id: number,
    textKeys: string[] = ['descripcion']
  ): string | number {
    if (!Number.isFinite(id) || id <= 0) {
      return '';
    }

    const match = (catalog ?? []).find((item) => Number(item?.pk ?? item?.id ?? 0) === id);
    if (!match) {
      return id;
    }

    for (const key of textKeys) {
      const value = `${match?.[key] ?? ''}`.trim();
      if (value) {
        return value;
      }
    }

    return id;
  }

  private getYesNoLabel(value: unknown): string {
    const normalized = String(value ?? '').trim().toUpperCase();
    if (normalized === 'S') {
      return 'Sí';
    }

    if (normalized === 'N') {
      return 'No';
    }

    return normalized;
  }

  private toExportNumber(value: unknown): number | string {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : '';
  }

  private toExportText(value: unknown): string {
    return value == null ? '' : String(value);
  }

  private calculateExportColumnWidth(
    header: string,
    rows: Record<string, string | number>[]
  ): number {
    const maxValueLength = rows.reduce((max, row) => {
      const value = row[header];
      const cellText = value == null ? '' : String(value);
      return Math.max(max, cellText.length);
    }, header.length);

    const isLongTextColumn = /nombre|direccion|comentario|referencia|informacion/i.test(header);
    const maxWidth = isLongTextColumn ? 42 : 26;

    return Math.min(Math.max(maxValueLength + 2, 14), maxWidth);
  }

  private getReportHeaders(
    rows: Record<string, any>[],
    response?: SourceReportResponse | ApiResponse<Record<string, any>[]> | ApiResponse<SourceReportResponse> | null
  ): string[] {
    const responseColumns = this.extractReportColumns(response);

    if (responseColumns.length) {
      const extraColumns = Array.from(
        new Set(
          rows.flatMap((row) => Object.keys(row)).filter(
            (column) => !responseColumns.includes(column)
          )
        )
      );

      return [...responseColumns, ...extraColumns];
    }

    const fixedColumns = this.reportFixedColumns.filter((column) =>
      rows.some((row) => Object.prototype.hasOwnProperty.call(row, column))
    );

    const dynamicColumns = Array.from(
      new Set(
        rows.flatMap((row) => Object.keys(row)).filter(
          (column) => !fixedColumns.includes(column)
        )
      )
    );

    return [...fixedColumns, ...dynamicColumns];
  }

  private normalizeReportRows(rows: Record<string, any>[], headers: string[]): Record<string, any>[] {
    return rows.map((row) =>
      headers.reduce<Record<string, any>>((normalizedRow, header) => {
        normalizedRow[header] = row[header] ?? '';
        return normalizedRow;
      }, {})
    );
  }

  private formatReportHeaderLabel(header: string): string {
    const knownLabels: Record<string, string> = {
      fuente_pk: 'Fuente PK',
      fuente_codigo_INE: 'Codigo INE Fuente',
      fuente_razon_social: 'Razon Social Fuente',
      fuente_nombre: 'Nombre Fuente',
      fuente_direccion: 'Direccion Fuente',
      fuente_zona: 'Zona Fuente',
      articulo_pk: 'Articulo PK',
      articulo_codigo_INE: 'Codigo INE Articulo',
      articulo_nombre: 'Nombre Articulo',
      unidad_medida_pk: 'Unidad Medida PK',
      unidad_medida: 'Unidad de Medida',
      factor_conversion: 'Factor de Conversion',
      articulo_cantidad: 'Cantidad Articulo',
      articulo_precio: 'Precio Articulo',
      articulo_comentarios: 'Comentarios Articulo',
      articulo_estado: 'Estado Articulo'
    };

    if (knownLabels[header]) {
      return knownLabels[header];
    }

    return header
      .replace(/_/g, ' ')
      .replace(/\bpk\b/gi, 'PK')
      .replace(/\bine\b/gi, 'INE')
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  private calculateReportColumnWidth(
    header: string,
    rows: Record<string, any>[]
  ): number {
    const displayHeader = this.formatReportHeaderLabel(header);
    const maxValueLength = rows.reduce((max, row) => {
      const value = row[header];
      const cellText = value == null ? '' : String(value);
      return Math.max(max, cellText.length);
    }, displayHeader.length);

    const isLongTextColumn = /comentario|direccion|nombre/i.test(header);
    const maxWidth = isLongTextColumn ? 50 : 30;

    return Math.min(Math.max(maxValueLength + 2, 14), maxWidth);
  }

  private extractReportRows(
    response:
      | SourceReportResponse
      | Record<string, any>[]
      | ApiResponse<Record<string, any>[]>
      | ApiResponse<SourceReportResponse>
      | null
      | undefined
  ): Record<string, any>[] | null {
    if (Array.isArray(response)) {
      return response;
    }

    const candidate = response as {
      payload?: unknown;
      data?: unknown;
    } | null | undefined;

    if (Array.isArray(candidate?.payload)) {
      return candidate.payload as Record<string, any>[];
    }

    const nestedData = candidate?.data;

    if (nestedData && typeof nestedData === 'object') {
      const nestedCandidate = nestedData as { payload?: unknown };

      if (Array.isArray(nestedCandidate.payload)) {
        return nestedCandidate.payload as Record<string, any>[];
      }
    }

    if (Array.isArray(nestedData)) {
      return nestedData as Record<string, any>[];
    }

    return null;
  }

  private extractReportColumns(
    response?:
      | SourceReportResponse
      | ApiResponse<Record<string, any>[]>
      | ApiResponse<SourceReportResponse>
      | null
  ): string[] {
    const candidate = response as {
      columns?: unknown;
      data?: unknown;
    } | null | undefined;

    if (Array.isArray(candidate?.columns)) {
      return candidate.columns.filter(
        (column: unknown): column is string => typeof column === 'string' && !!column.trim()
      );
    }

    const nestedData = candidate?.data;

    if (nestedData && typeof nestedData === 'object') {
      const nestedCandidate = nestedData as { columns?: unknown };

      if (Array.isArray(nestedCandidate.columns)) {
        return nestedCandidate.columns.filter(
          (column: unknown): column is string => typeof column === 'string' && !!column.trim()
        );
      }
    }

    return [];
  }

  private extractReportMessage(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const message = (response as { message?: unknown }).message;
    return typeof message === 'string' && message.trim() ? message.trim() : null;
  }

  private countArticleCharacteristics(articles: SurveyArticulo[]): number {
    return (articles ?? []).reduce(
      (total, article) => total + (article.characteristics?.length ?? 0),
      0
    );
  }

  private saveArticlesSequentially(sourceId: number, articles: SurveyArticulo[]) {
    const userId = getCurrentUserId();

    return from(articles).pipe(
      concatMap(article =>
        this.sourceArticlesService.getBySource(sourceId).pipe(
          switchMap(response => {
            const existing = (response.data ?? [])
              .find(item => Number(item.fkArticulo) === Number(article.articleId));

            const sourceArticle$ = existing
              ? this.sourceArticlesService.update(existing.pk, {
                  fkFuente: sourceId,
                  fkArticulo: Number(article.articleId),
                  fkUnidadMedida: Number(article.unitMeasureId),
                  cantidad: Number(article.cantidad ?? existing.cantidad ?? 0),
                  precio: Number(article.precio ?? existing.precio ?? 0),
                  comentarios: (article.comments ?? '').trim(),
                  estado: existing.estado ?? 'A',
                  fkUsuario: userId,
                  fkTransaccion: 3
                })
              : this.sourceArticlesService.create({
                  fkFuente: sourceId,
                  fkArticulo: Number(article.articleId),
                  fkUnidadMedida: Number(article.unitMeasureId),
                  cantidad: Number(article.cantidad ?? 0),
                  precio: Number(article.precio ?? 0),
                  comentarios: (article.comments ?? '').trim(),
                  estado: 'A',
                  fkUsuario: userId,
                  fkTransaccion: 3
                });

            return sourceArticle$.pipe(
              switchMap(sourceArticleResponse => {
                const sourceArticle = sourceArticleResponse.data;
                const sourceArticleId = Number(sourceArticle?.pk ?? existing?.pk ?? 0);

                return this.sourceArticleCharacteristicsService.getBySourceArticle(sourceArticleId).pipe(
                  switchMap(characteristicsResponse =>
                    from(article.characteristics ?? []).pipe(
                      concatMap(characteristic => {
                        const existingCharacteristic = (characteristicsResponse.data ?? [])
                          .find(item => Number(item.fkCaracteristica) === Number(characteristic.characteristicId));

                        const payload = {
                          fkCaracteristica: Number(characteristic.characteristicId),
                          codigoCaracteristicaValor:
                            characteristic.characteristicValueCode != null
                              ? Number(characteristic.characteristicValueCode)
                              : null,
                          valor: (characteristic.value ?? '').trim(),
                          estado: existingCharacteristic?.estado ?? 'A' as const,
                          fkUsuario: userId,
                          fkTransaccion: 1
                        };

                        return existingCharacteristic
                          ? this.sourceArticleCharacteristicsService.update(existingCharacteristic.pk, payload)
                          : this.sourceArticleCharacteristicsService.create(sourceArticleId, payload);
                      }),
                      toArray(),
                      map(() => sourceArticleResponse)
                    )
                  )
                );
              })
            );
          })
        )
      ),
      toArray()
    );
  }

  isStatusFilterDisabled(): boolean {
    return Boolean(this.searchForm.value.term?.trim());
  }

  initTooltips(): void {
    setTimeout(() => {
      const bootstrap = (window as any).bootstrap;
      if (!bootstrap?.Tooltip) return;

      const elements = Array.from(
        document.querySelectorAll('[data-app-tooltip="true"]')
      );

      elements.forEach(element => {
        const tooltip = bootstrap.Tooltip.getOrCreateInstance(element, {
          trigger: 'hover focus'
        });

        if (!(element as HTMLElement).dataset['tooltipBound']) {
          element.addEventListener('shown.bs.tooltip', () => {
            setTimeout(() => tooltip.hide(), 2200);
          });
          (element as HTMLElement).dataset['tooltipBound'] = 'true';
        }
      });
    });
  }


}
